import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookMarked, Download, Eye, FileSpreadsheet, Loader2, Lock, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { useIsAdminTier, useIsReadOnly, useProfile } from '@/shared/auth/SessionProvider';
import { isNovaCaja } from '@/features/tesoreria/lib/permissions';
import { LlevaFacturaTag } from '@/features/tesoreria/components/LlevaFacturaTag';
import { CajaNombre } from '@/features/tesoreria/components/CajaNombre';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import {
  puedeReclasificar,
  ReclasificarCuentaDialog,
} from '@/features/tesoreria/components/ReclasificarCuentaDialog';
import { ImportarReclasificacionDialog } from './ImportarReclasificacionDialog';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Input } from '@/shared/components/ui/input';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { SortableTh } from '@/shared/components/ui/sortable-th';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { coincideBusqueda, prepararBusqueda, useOrdenTabla } from '@/shared/lib/tablaOrden';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';
import {
  downloadCierreMovimientosXlsx,
  importarCierreMovimientos,
  listCierresAnios,
  listCierresDeCaja,
  previewCierreMovimientos,
  type CierreMovimiento,
  type CierreSummary,
} from './api';

/**
 * Cierres de Caja report — home-banking style browser. The admin picks a
 * year + a caja, sees every cierre that was done in that window, opens a
 * preview for any cierre to see the movimientos it closed, and can
 * export those movimientos to Excel (.xlsx).
 *
 * Composition:
 *   - `useCierresFilters()` — owns anio/caja state + localStorage round-trip
 *   - `<CierresDeCajaToolbar />` — renders the Año + Caja + Refresh controls
 *   - `<CierresDeCajaBody />` — renders the cierres list + preview dialog
 *
 * Both pieces consume the same `filters` object so ReportesPage can drop
 * the toolbar inline next to the report dropdown (single row) and the
 * body underneath, without needing context or two parallel state copies.
 *
 * Year list is server-derived — only years that actually have cierres
 * appear in the dropdown. Caja list reuses Tesorería's CajaSelect data
 * source.
 */

export type CierresFilters = {
  anio: number | null;
  caja: string;
};

const CIERRES_STORAGE_KEY_PREFIX = 'fornitalia.reportes.cierres';

function readStored(storageKey: string): CierresFilters {
  if (typeof window === 'undefined') return { anio: null, caja: '' };
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { anio: null, caja: '' };
    const parsed = JSON.parse(raw) as Partial<CierresFilters>;
    return {
      anio: Number.isFinite(parsed.anio) ? Number(parsed.anio) : null,
      caja: typeof parsed.caja === 'string' ? parsed.caja : '',
    };
  } catch {
    return { anio: null, caja: '' };
  }
}

export function useCierresFilters() {
  const { config: activeCountry } = useActiveCountry();
  // Country-scoped storage: AR cajas (Transferencia Galicia, Efectivo
  // Pesos, …) don't exist in MX and vice versa. A shared key persisted
  // an MX-only "Caja Diferencia" into AR after a country switch and the
  // dropdown rendered the value despite it being absent from the AR
  // catalog. Mirrors the same fix Tesorería already uses.
  const storageKey = `${CIERRES_STORAGE_KEY_PREFIX}.${activeCountry.code}`;
  const [anio, setAnio] = useState<number | null>(() => readStored(storageKey).anio);
  const [caja, setCaja] = useState<string>(() => readStored(storageKey).caja);

  // Re-hydrate when active country changes — useState's initializer
  // value was captured at first mount and wouldn't reflect the new
  // country-scoped key.
  useEffect(() => {
    const next = readStored(storageKey);
    setAnio(next.anio);
    setCaja(next.caja);
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ anio, caja }),
    );
  }, [storageKey, anio, caja]);

  return { anio, caja, setAnio, setCaja };
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

export function CierresDeCajaToolbar({
  filters,
  onChange,
}: {
  filters: CierresFilters;
  onChange: (next: CierresFilters) => void;
}) {
  const { anio, caja } = filters;
  const { config: activeCountry } = useActiveCountry();
  const isNova = useProfile()?.perfil === 'nova';

  const aniosQ = useQuery({
    queryKey: ['reportes', 'cierres-anios', activeCountry.code] as const,
    queryFn: listCierresAnios,
    staleTime: 5 * 60_000,
  });

  // Default the year to the most recent one available (server returns
  // sorted desc) the first time the list lands and we don't have a
  // persisted preference yet.
  useEffect(() => {
    if (anio != null) return;
    const first = aniosQ.data?.[0];
    if (first != null) onChange({ ...filters, anio: first });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aniosQ.data, anio]);

  const mediosQ = useQuery({
    queryKey: ['reportes', 'cierres-medios', activeCountry.code] as const,
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });

  // Nova reads cierres for its own cajas only — `reportes.ts` 403s on
  // anything else, so showing them here would just be a dead dropdown.
  const selectableMedios = useMemo(
    () =>
      (mediosQ.data ?? []).filter(
        (m) => !isNova || isNovaCaja(m.fields.Medio_MDP),
      ),
    [mediosQ.data, isNova],
  );

  const groupedMedios = useMemo(() => {
    const active = selectableMedios.filter((m) => m.fields.Status_MDP === 'Activo');
    return {
      local: active.filter((m) => m.fields.Categoria_MDP === 'Local'),
      intl: active.filter((m) => m.fields.Categoria_MDP === 'Internacional'),
    };
  }, [selectableMedios]);

  // Auto-pick a caja the first time medios load, OR when the
  // currently-selected caja isn't selectable in the active country /
  // role (e.g. user picked "Caja Diferencia" in MX, switched to AR).
  useEffect(() => {
    if (selectableMedios.length === 0) return;
    const names = new Set(
      selectableMedios.map((m) => m.fields.Medio_MDP ?? '').filter(Boolean),
    );
    if (caja && names.has(caja)) return;
    const preferred =
      selectableMedios.find((m) => m.fields.Medio_MDP === 'Efectivo Pesos') ??
      selectableMedios.find((m) => m.fields.Status_MDP === 'Activo') ??
      selectableMedios[0];
    const name = preferred?.fields.Medio_MDP ?? '';
    if (name && name !== caja) onChange({ ...filters, caja: name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableMedios, caja]);

  return (
    <>
      <div className="min-w-[7rem] sm:w-28">
        <Select
          value={anio != null ? String(anio) : undefined}
          onValueChange={(v) => onChange({ ...filters, anio: Number(v) })}
          disabled={aniosQ.isPending || (aniosQ.data?.length ?? 0) === 0}
        >
          <SelectTrigger className="h-12">
            <SelectValue
              placeholder={aniosQ.isPending ? 'Cargando…' : 'Año'}
            />
          </SelectTrigger>
          <SelectContent>
            {(aniosQ.data ?? []).map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[12rem] sm:w-56">
        <Select
          value={caja || undefined}
          onValueChange={(v) => onChange({ ...filters, caja: v })}
          disabled={mediosQ.isPending}
        >
          <SelectTrigger className="h-12">
            <SelectValue
              placeholder={mediosQ.isPending ? 'Cargando…' : 'Seleccionar caja…'}
            />
          </SelectTrigger>
          <SelectContent>
            {groupedMedios.local.length > 0 && (
              <SelectGroup>
                <SelectLabel>Pesos</SelectLabel>
                {groupedMedios.local.map((m) => (
                  <SelectItem
  key={m.id}
  value={m.fields.Medio_MDP ?? ''}
  trailing={<LlevaFacturaTag show={m.fields.LlevaFactura_MDP === 'SI'} />}
>
  <CajaNombre caja={m.fields.Medio_MDP} tipo={m.fields.Tipo_MDP} />
</SelectItem>
                ))}
              </SelectGroup>
            )}
            {groupedMedios.intl.length > 0 && (
              <SelectGroup>
                <SelectLabel>Dólares</SelectLabel>
                {groupedMedios.intl.map((m) => (
                  <SelectItem
  key={m.id}
  value={m.fields.Medio_MDP ?? ''}
  trailing={<LlevaFacturaTag show={m.fields.LlevaFactura_MDP === 'SI'} />}
>
  <CajaNombre caja={m.fields.Medio_MDP} tipo={m.fields.Tipo_MDP} />
</SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Body (cierres list + preview dialog)
// ---------------------------------------------------------------------------

type ColumnaCierre = 'fecha' | 'usuario' | 'movimientos' | 'ingresos' | 'egresos' | 'saldo';

function valorCierre(c: CierreSummary, col: ColumnaCierre): unknown {
  switch (col) {
    case 'fecha':
      return c.fecha;
    case 'usuario':
      return c.usuario;
    case 'movimientos':
      return c.movimientosCount;
    case 'ingresos':
      return c.ingresos;
    case 'egresos':
      return c.egresos;
    case 'saldo':
      return c.saldoCerrado;
  }
}

type ColumnaMovCierre = 'fecha' | 'tipo' | 'cuenta' | 'descripcion' | 'cliente' | 'usuario' | 'monto';

function valorMovCierre(r: CierreMovimiento, col: ColumnaMovCierre): unknown {
  switch (col) {
    case 'fecha':
      return r.Fecha;
    case 'tipo':
      return r.Tipo;
    case 'cuenta':
      return r['Cuenta Contable'];
    case 'descripcion':
      return r.Descripcion;
    case 'cliente':
      return r.Cliente;
    case 'usuario':
      return r.Usuario;
    case 'monto':
      return r.Monto;
  }
}

/** Mismas clases que el `Th` local, para los encabezados que ordenan. */
const TH_CLS = 'px-4 py-3 font-semibold';

export function CierresDeCajaBody({ filters }: { filters: CierresFilters }) {
  const { anio, caja } = filters;
  const [previewing, setPreviewing] = useState<CierreSummary | null>(null);
  // `downloadingId` is the `idCierreCaja` of the row whose export is
  // currently being fetched. Per-row state so one slow export doesn't
  // disable every download button — only the spinning one.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleRowDownload = async (c: CierreSummary) => {
    setDownloadingId(c.idCierreCaja);
    try {
      const safeName = c.idCierreCaja.replace(/[^A-Za-z0-9_-]/g, '_');
      await downloadCierreMovimientosXlsx(
        c.idCierreCaja,
        `cierre_${safeName}.xlsx`,
      );
      toast.success('Movimientos exportados');
    } catch (err) {
      toast.error('No se pudo exportar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const cierresQ = useQuery({
    queryKey: ['reportes', 'cierres-list', anio, caja],
    queryFn: () => listCierresDeCaja(anio ?? 0, caja),
    enabled: anio != null && Boolean(caja),
    staleTime: 30_000,
  });

  const cierres = cierresQ.data ?? [];
  const { ordenadas: cierresOrdenados, orden, alternar } = useOrdenTabla(cierres, valorCierre);
  const isEnabled = anio != null && Boolean(caja);
  // Show the skeleton in two cases:
  //  - First fetch in flight (no data yet) — the normal initial-load
  //    case.
  //  - We have an empty cached result AND a background refetch is in
  //    flight. This covers the "stale empty cache" case where TanStack
  //    Query serves an old `[]` instantly, then revalidates: without
  //    this branch the "no hay cierres" empty state flashes before the
  //    refetch arrives with the real data. Surfaced 2026-05-13 after a
  //    schema bug fix left empty results cached for cajas that actually
  //    had cierres.
  const loading =
    isEnabled &&
    (cierresQ.isPending ||
      (cierres.length === 0 && cierresQ.isFetching));

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
        {/* Desktop: full table with all columns. */}
        <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <SortableTh label="Fecha" sortKey="fecha" orden={orden} onSort={alternar} className={TH_CLS} />
                <SortableTh label="Usuario" sortKey="usuario" orden={orden} onSort={alternar} className={TH_CLS} />
                <SortableTh label="Movimientos" sortKey="movimientos" orden={orden} onSort={alternar} className={cn(TH_CLS, 'text-right')} />
                <SortableTh label="Ingresos" sortKey="ingresos" orden={orden} onSort={alternar} className={cn(TH_CLS, 'text-right')} />
                <SortableTh label="Egresos" sortKey="egresos" orden={orden} onSort={alternar} className={cn(TH_CLS, 'text-right')} />
                <SortableTh label="Saldo cerrado" sortKey="saldo" orden={orden} onSort={alternar} className={cn(TH_CLS, 'text-right')} />
                <Th className="w-28 text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <SkeletonRows cols={7} />
              ) : cierresQ.isError ? (
                <RowStatus cols={7}>
                  <span className="text-destructive">
                    No se pudo cargar la lista de cierres.
                  </span>
                </RowStatus>
              ) : !anio || !caja ? (
                <RowStatus cols={7}>
                  Seleccioná año y caja para ver los cierres.
                </RowStatus>
              ) : cierres.length === 0 ? (
                <RowStatus cols={7}>
                  No hay cierres en {anio} para {etiquetaCaja(caja)}.
                </RowStatus>
              ) : (
                cierresOrdenados.map((c) => {
                  const isDownloading = downloadingId === c.idCierreCaja;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setPreviewing(c)}
                      className="cursor-pointer transition-colors hover:bg-muted/40"
                    >
                      <Td className="font-medium text-foreground">
                        {formatFecha(c.fecha)}
                      </Td>
                      <Td className="text-muted-foreground">{c.usuario ?? '—'}</Td>
                      <Td className="text-right text-muted-foreground tabular-nums">
                        {c.movimientosCount}
                      </Td>
                      <Td className="text-right text-emerald-700 tabular-nums">
                        {formatMoney(c.ingresos, c.currency)}
                      </Td>
                      <Td className="text-right text-rose-700 tabular-nums">
                        {formatMoney(c.egresos, c.currency)}
                      </Td>
                      <Td className="text-right font-semibold text-foreground tabular-nums">
                        {formatMoney(c.saldoCerrado, c.currency)}
                      </Td>
                      <Td className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewing(c);
                            }}
                            aria-label="Ver detalle"
                            title="Ver detalle"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <Eye className="h-4 w-4" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRowDownload(c);
                            }}
                            disabled={isDownloading}
                            aria-label="Exportar a Excel"
                            title="Exportar a Excel"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60"
                          >
                            {isDownloading ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <Download className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: card per cierre. Same data, less horizontal density. */}
        <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
          <MobileCardShell
            loading={loading}
            error={cierresQ.isError}
            empty={!loading && cierres.length === 0}
            emptyMessage={
              !anio || !caja
                ? 'Seleccioná año y caja para ver los cierres.'
                : `No hay cierres en ${anio} para ${etiquetaCaja(caja)}.`
            }
            errorMessage="No se pudo cargar la lista de cierres."
          >
            {cierresOrdenados.map((c) => {
              const isDownloading = downloadingId === c.idCierreCaja;
              return (
                <li
                  key={c.id}
                  onClick={() => setPreviewing(c)}
                  className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-muted/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {formatFecha(c.fecha)}
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
                      {formatMoney(c.saldoCerrado, c.currency)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {c.usuario ?? '—'} · {c.movimientosCount} movimiento
                    {c.movimientosCount === 1 ? '' : 's'}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="text-emerald-700 tabular-nums">
                      ↑ {formatMoney(c.ingresos, c.currency)}
                    </span>
                    <span className="text-rose-700 tabular-nums">
                      ↓ {formatMoney(c.egresos, c.currency)}
                    </span>
                    <div className="ml-auto inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewing(c);
                        }}
                        aria-label="Ver detalle"
                        title="Ver detalle"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRowDownload(c);
                        }}
                        disabled={isDownloading}
                        aria-label="Exportar a Excel"
                        title="Exportar a Excel"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-wait disabled:opacity-60"
                      >
                        {isDownloading ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Download className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </MobileCardShell>
        </div>
      </div>

      {previewing && (
        <CierrePreviewDialog
          cierre={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Preview dialog — movimientos for a single cierre + XLSX export
// ---------------------------------------------------------------------------

function CierrePreviewDialog({
  cierre,
  onClose,
}: {
  cierre: CierreSummary;
  onClose: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  // Reimputar desde el reporte: el cierre ya está cerrado, pero reclasificar
  // no mueve plata — el saldo cerrado y los totales quedan intactos.
  const isReadOnly = useIsReadOnly();
  const isAdminTier = useIsAdminTier();
  const puedeEditarCuenta = !isReadOnly && isAdminTier;
  const [reclasificando, setReclasificando] = useState<CierreMovimiento | null>(null);
  const [importando, setImportando] = useState(false);
  const colCount = puedeEditarCuenta ? 8 : 7;
  const previewQ = useQuery({
    queryKey: ['reportes', 'cierre-preview', cierre.idCierreCaja],
    queryFn: () => previewCierreMovimientos(cierre.idCierreCaja),
    staleTime: 60_000,
  });
  const [busqueda, setBusqueda] = useState('');
  const movimientos = useMemo(() => previewQ.data?.rows ?? [], [previewQ.data]);
  const filtrados = useMemo(() => {
    const b = prepararBusqueda(busqueda);
    return movimientos.filter((r) =>
      coincideBusqueda(
        [r.Fecha, r.Tipo, r.Categoria, r['Cuenta Contable'], r.Descripcion, r.Cliente, r.Observaciones, r.Usuario, r.Status, r.Monto, r.Moneda],
        b,
      ),
    );
  }, [movimientos, busqueda]);
  const { ordenadas: movOrdenados, orden, alternar } = useOrdenTabla(filtrados, valorMovCierre);
  const sinCoincidencias = 'Sin coincidencias para la búsqueda.';
  const sortTh = (sortKey: ColumnaMovCierre, label: string, className?: string) => (
    <SortableTh label={label} sortKey={sortKey} orden={orden} onSort={alternar} className={cn(TH_CLS, className)} />
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const safeName = cierre.idCierreCaja.replace(/[^A-Za-z0-9_-]/g, '_');
      await downloadCierreMovimientosXlsx(
        cierre.idCierreCaja,
        `cierre_${safeName}.xlsx`,
      );
      toast.success('Movimientos exportados');
    } catch (err) {
      toast.error('No se pudo exportar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-[95vw] flex-col lg:max-w-7xl">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground">
              <Lock className="h-5 w-5" aria-hidden />
            </span>
            <div className="flex-1">
              <DialogTitle>
                {etiquetaCaja(cierre.caja)} · {formatFecha(cierre.fecha)}
              </DialogTitle>
              <DialogDescription>
                {cierre.movimientosCount} movimientos · Saldo cerrado{' '}
                {formatMoney(cierre.saldoCerrado, cierre.currency)} · Cerrado por{' '}
                {cierre.usuario ?? '—'}
              </DialogDescription>
            </div>
          </div>
          <div className="relative mt-2 sm:w-72">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              variant="filled"
              placeholder="Buscar"
              className="pl-9"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label="Buscar en los movimientos del cierre"
            />
          </div>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1 overflow-y-auto p-0">
          {/* Desktop: full table. Wide enough to keep all columns. */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {sortTh('fecha', 'Fecha')}
                  {sortTh('tipo', 'Tipo')}
                  {/* La celda muestra el PAR porque es lo que este reporte
                      deja editar y en el plan de cuentas las dos van juntas.
                      Mismo orden que el diálogo de edición y que
                      Configuración → Cuentas Contables: cuenta arriba,
                      categoría abajo. */}
                  {sortTh('cuenta', 'Cuenta contable / Categoría')}
                  {sortTh('descripcion', 'Descripción')}
                  {sortTh('cliente', 'Cliente')}
                  {sortTh('usuario', 'Usuario')}
                  {sortTh('monto', 'Monto', 'text-right')}
                  {puedeEditarCuenta && <Th className="w-10" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {previewQ.isPending ? (
                  <SkeletonRows cols={colCount} />
                ) : previewQ.isError ? (
                  <RowStatus cols={colCount}>
                    <span className="text-destructive">
                      No se pudieron cargar los movimientos.
                    </span>
                  </RowStatus>
                ) : (previewQ.data?.rows.length ?? 0) === 0 ? (
                  <RowStatus cols={colCount}>
                    Sin movimientos en este cierre.
                  </RowStatus>
                ) : movOrdenados.length === 0 ? (
                  <RowStatus cols={colCount}>{sinCoincidencias}</RowStatus>
                ) : (
                  movOrdenados.map((r, i) => {
                    const isIngreso = r.Tipo === 'Ingreso';
                    const isEgreso = r.Tipo === 'Egreso';
                    return (
                      <tr key={i} className="hover:bg-muted/30">
                        <Td className="whitespace-nowrap text-muted-foreground">
                          {r.Fecha}
                        </Td>
                        <Td
                          className={cn(
                            'whitespace-nowrap',
                            isIngreso && 'text-emerald-700',
                            isEgreso && 'text-rose-700',
                            !isIngreso && !isEgreso && 'text-foreground',
                          )}
                        >
                          {r.Tipo}
                        </Td>
                        {/* La cuenta contable es justamente lo que este
                            reporte ahora deja editar, así que tiene que verse
                            sin abrir el diálogo. Va debajo de la categoría,
                            igual que en la grilla de Tesorería. */}
                        <Td className="text-muted-foreground">
                          <div>{r['Cuenta Contable'] || '—'}</div>
                          {r.Categoria && (
                            <div className="text-xs opacity-75">{r.Categoria}</div>
                          )}
                        </Td>
                        <Td
                          className="max-w-xs truncate text-foreground"
                          title={r.Descripcion}
                        >
                          {r.Descripcion || '—'}
                        </Td>
                        <Td className="text-muted-foreground">{r.Cliente || '—'}</Td>
                        <Td className="whitespace-nowrap text-muted-foreground">{r.Usuario || '—'}</Td>
                        <Td className="whitespace-nowrap text-right font-medium tabular-nums">
                          {r.Monto} {r.Moneda}
                        </Td>
                        {puedeEditarCuenta && (
                          <Td className="text-right">
                            {puedeReclasificar({ categoria: r.Categoria, status: r.Status }) && (
                              <button
                                type="button"
                                onClick={() => setReclasificando(r)}
                                title={`Cambiar cuenta contable (actual: ${r['Cuenta Contable'] || '—'})`}
                                aria-label="Cambiar cuenta contable"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sky-300 bg-sky-50 text-sky-700 transition-colors hover:bg-sky-100"
                              >
                                <BookMarked className="h-3.5 w-3.5" aria-hidden />
                              </button>
                            )}
                          </Td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: card per movimiento. Compacts the table fields into
              two stacked lines + a tinted monto. */}
          <div className="md:hidden">
            <MobileCardShell
              loading={previewQ.isPending}
              error={previewQ.isError}
              empty={movOrdenados.length === 0}
              emptyMessage={movimientos.length === 0 ? 'Sin movimientos en este cierre.' : sinCoincidencias}
              errorMessage="No se pudieron cargar los movimientos."
            >
              {movOrdenados.map((r, i) => {
                const isIngreso = r.Tipo === 'Ingreso';
                const isEgreso = r.Tipo === 'Egreso';
                return (
                  <li key={i} className="flex flex-col gap-1.5 px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-muted-foreground tabular-nums">
                          {r.Fecha}
                        </span>
                        <span
                          className={cn(
                            'rounded-md px-2 py-0.5 font-semibold',
                            isIngreso && 'bg-emerald-100 text-emerald-700',
                            isEgreso && 'bg-rose-100 text-rose-700',
                            !isIngreso && !isEgreso && 'bg-secondary text-foreground',
                          )}
                        >
                          {r.Tipo}
                        </span>
                        {r.Categoria && (
                          <span className="text-muted-foreground">{r.Categoria}</span>
                        )}
                      </div>
                      <span
                        className={cn(
                          'shrink-0 text-sm font-semibold tabular-nums',
                          isIngreso && 'text-emerald-700',
                          isEgreso && 'text-rose-700',
                          !isIngreso && !isEgreso && 'text-foreground',
                        )}
                      >
                        {r.Monto} {r.Moneda}
                      </span>
                    </div>
                    <div className="text-sm text-foreground">
                      {r.Descripcion || '—'}
                    </div>
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0 text-xs text-muted-foreground">
                        {(r.Cliente || r.Usuario) && (
                          <>
                            {r.Cliente ? r.Cliente : '—'}
                            {r.Usuario ? ` · ${r.Usuario}` : ''}
                          </>
                        )}
                        {r['Cuenta Contable'] && (
                          <div className="truncate">{r['Cuenta Contable']}</div>
                        )}
                      </div>
                      {puedeEditarCuenta &&
                        puedeReclasificar({ categoria: r.Categoria, status: r.Status }) && (
                          <button
                            type="button"
                            onClick={() => setReclasificando(r)}
                            title="Cambiar cuenta contable"
                            aria-label="Cambiar cuenta contable"
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-sky-300 bg-sky-50 text-sky-700 transition-colors hover:bg-sky-100"
                          >
                            <BookMarked className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        )}
                    </div>
                  </li>
                );
              })}
            </MobileCardShell>
          </div>
        </DialogBody>
        {/* Override the footer's default `flex-col-reverse` on mobile
            so DOM order matches visual order — totals on top, then
            Exportar a Excel, then Cerrar at the bottom. Desktop keeps the
            shadcn default (row, right-aligned). */}
        <DialogFooter className="flex-col sm:flex-row">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground sm:mr-auto">
            {(previewQ.data?.totals ?? []).map((t) => (
              <span key={t.label}>
                <span className="text-muted-foreground">{t.label}: </span>
                <span className="font-semibold text-foreground">{t.value}</span>
              </span>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleDownload}
            disabled={downloading || (previewQ.data?.rows.length ?? 0) === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
            Exportar a Excel
          </Button>
          {puedeEditarCuenta && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportando(true)}
              className="gap-2"
            >
              <Upload className="h-4 w-4" aria-hidden />
              Importar Excel
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
      {importando && (
        <ImportarReclasificacionDialog
          contexto={`${etiquetaCaja(cierre.caja)} · ${cierre.fecha}`}
          origen="Cierres de Caja"
          subirArchivo={(file) => importarCierreMovimientos(cierre.idCierreCaja, file)}
          onClose={() => setImportando(false)}
          onAplicado={() => previewQ.refetch()}
        />
      )}
      {reclasificando && (
        <ReclasificarCuentaDialog
          mov={{
            id: reclasificando.Id,
            categoria: reclasificando.Categoria || null,
            cuentaContable: reclasificando['Cuenta Contable'] || null,
            descripcion: reclasificando.Descripcion || null,
            fecha: reclasificando.Fecha || null,
            tipo: reclasificando.Tipo || null,
            montoTexto: `${reclasificando.Monto} ${reclasificando.Moneda}`.trim(),
            contexto: `${etiquetaCaja(cierre.caja)} · cierre ${cierre.fecha}`,
          }}
          onClose={() => setReclasificando(null)}
          onDone={() => previewQ.refetch()}
        />
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell helpers (local — keep this file self-contained)
// ---------------------------------------------------------------------------

function Th({
  children,
  className,
  'aria-hidden': ariaHidden,
}: {
  children?: React.ReactNode;
  className?: string;
  'aria-hidden'?: boolean;
}) {
  return (
    <th className={cn('px-4 py-3 font-semibold', className)} aria-hidden={ariaHidden}>
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td className={cn('px-4 py-3', className)} title={title}>
      {children}
    </td>
  );
}

function RowStatus({
  children,
  cols,
}: {
  children: React.ReactNode;
  cols: number;
}) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <Skeleton className="h-4 w-full max-w-[140px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Wraps the loading/error/empty states for the mobile-card view so each
 * caller only writes the per-row card markup. Mirrors the
 * `MobileCardShell` pattern in AprobacionesPage.tsx — kept local here
 * instead of cross-importing because they're independent features.
 */
function MobileCardShell({
  loading,
  error,
  empty,
  emptyMessage,
  errorMessage = 'No se pudieron cargar los datos.',
  children,
}: {
  loading: boolean;
  error: boolean;
  empty: boolean;
  emptyMessage: string;
  errorMessage?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-sm text-destructive">{errorMessage}</div>
    );
  }
  if (empty) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
    );
  }
  return <ul className="divide-y divide-border">{children}</ul>;
}

/** `2026-04-30` → `30/04/2026`. Already-formatted strings pass through. */
function formatFecha(raw: string): string {
  if (!raw) return '—';
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
